import { stringToNumber } from "@/helpers/utils";
import tagStyles from "@/components/tags/styles.module.scss";
import { cn } from "@/lib/utils";
import { Tooltip } from "@altimateai/lego";

// Tag styling constants (from ColorInsightTags)
const TAG_COLORS = ["blue", "orange", "yellow", "purple", "dark_green"];
const COLOR_MAP = {
  blue: tagStyles.level_blue,
  orange: tagStyles.level_orange,
  red: tagStyles.level_red,
  green: tagStyles.level_green,
  dark_green: tagStyles.level_dark_green,
  yellow: tagStyles.level_yellow,
  purple: tagStyles.level_purple,
};

interface BIUserReportInsightOrOpportunityTagProps {
  insightOrOpportunity: { name: string; reasons: string[] };
}

export function opportunityToTagsTooltipDisplayFormat(opportunity: {
  opportunity_name: string;
  description: string;
}) {
  return {
    name: opportunity.opportunity_name,
    reasons: [opportunity.description],
  };
}

export function insightToTagsTooltipDisplayFormat(insight: { name: string; description: string }) {
  return {
    name: insight.name,
    reasons: [insight.description],
  };
}

const BIUserReportInsightOrOpportunityTag = ({
  insightOrOpportunity,
}: BIUserReportInsightOrOpportunityTagProps) => {
  const displayName = insightOrOpportunity.name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l: string) => l.toUpperCase());
  const color = TAG_COLORS[stringToNumber(insightOrOpportunity.name, TAG_COLORS.length)];
  const colorClass = COLOR_MAP[color as keyof typeof COLOR_MAP] || color;

  const tooltipContent = () => {
    return (
      <div className="al-space-y-3 al-w-[400px]" role="tooltip" aria-label={`${displayName} tooltip content`}>
        <div>
          <h4 className="al-text-sm al-font-semibold al-text-gray-900 al-mb-2">{displayName}</h4>
          <div className="al-space-y-2">
            <p className="al-text-xs al-font-medium al-text-gray-700 al-mb-1">Reason:</p>
            <ul aria-label={`Reasons for ${displayName}`}>
              {insightOrOpportunity.reasons.map((reason, index) => (
                <li key={index}>
                  <p className="al-text-xs al-text-gray-600 al-leading-relaxed">{reason}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Tooltip title={<div>{tooltipContent()}</div>}>
      <button
        type="button"
        className="al-cursor-pointer al-relative al-border-0 al-bg-transparent al-p-0"
        aria-label={`${displayName} insight`}
        aria-describedby={`tooltip-${insightOrOpportunity.name}`}
      >
        <div className={cn(tagStyles.level_tag, colorClass)}>
          {insightOrOpportunity.name.length > 20
            ? `${insightOrOpportunity.name.slice(0, 18)}...`
            : insightOrOpportunity.name}
        </div>
      </button>
    </Tooltip>
  );
};

export default BIUserReportInsightOrOpportunityTag;
